import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api';
import { ChevronDown, ChevronRight, Save, Eye, AlertTriangle } from 'lucide-react';
import { QuizQuestionEditor } from './QuizQuestionEditor';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Content-Editor für Lektionen eines Moduls.
 * Zeigt alle Lektionen als Accordion, jede erweiterbar mit:
 *   - Titel (Input)
 *   - Dauer (Input)
 *   - Moderator-Skript für Lisa TTS (Textarea)
 *   - Content-HTML (Textarea mit Monospace-Font)
 *   - Knowledge-Checks als JSON (Textarea)
 */
export const CourseContentEditor = ({ moduleId, moduleTitle, contentStatus, onClose }) => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [lessonDrafts, setLessonDrafts] = useState({}); // { lessonId: {...changes} }
  const [savingId, setSavingId] = useState(null);
  const [modStatus, setModStatus] = useState(contentStatus || 'published');

  useEffect(() => {
    loadLessons();
     
  }, [moduleId]);

  const loadLessons = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/admin/modules/${moduleId}/lessons`);
      const data = await res.json();
      if (data.success) {
        const fullList = [];
        for (const meta of data.lessons) {
          const detail = await apiFetch(`${API}/api/admin/lessons/${meta.id}`).then((r) => r.json());
          if (detail.success) fullList.push(detail.lesson);
        }
        setLessons(fullList);
      }
    } catch (e) {
      toast({ title: 'Fehler beim Laden der Lektionen', description: String(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (lessonId, field, value) => {
    setLessonDrafts((prev) => ({
      ...prev,
      [lessonId]: { ...(prev[lessonId] || {}), [field]: value },
    }));
  };

  const getFieldValue = (lesson, field) => {
    const draft = lessonDrafts[lesson.id];
    if (draft && field in draft) return draft[field];
    return lesson[field] ?? '';
  };

  const saveLesson = async (lesson) => {
    const draft = lessonDrafts[lesson.id];
    if (!draft || Object.keys(draft).length === 0) {
      toast({ title: 'Keine Änderungen', description: 'Nichts zu speichern.' });
      return;
    }
    setSavingId(lesson.id);
    try {
      // Parse knowledge_checks JSON wenn vorhanden
      const patch = { ...draft };
      if (typeof patch.knowledge_checks === 'string') {
        try {
          patch.knowledge_checks = JSON.parse(patch.knowledge_checks);
        } catch {
          toast({ title: 'JSON-Fehler', description: 'Knowledge-Checks sind kein gültiges JSON.', variant: 'destructive' });
          setSavingId(null);
          return;
        }
      }
      const res = await apiFetch(`${API}/api/admin/lessons/${lesson.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Gespeichert', description: `${data.updated_fields.length} Feld(er) aktualisiert.` });
        // Refresh die Lektion
        const detail = await apiFetch(`${API}/api/admin/lessons/${lesson.id}`).then((r) => r.json());
        if (detail.success) {
          setLessons((prev) => prev.map((l) => (l.id === lesson.id ? detail.lesson : l)));
        }
        setLessonDrafts((prev) => {
          const next = { ...prev };
          delete next[lesson.id];
          return next;
        });
      } else {
        toast({ title: 'Speichern fehlgeschlagen', description: data.detail || 'Unbekannter Fehler', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Speichern fehlgeschlagen', description: String(e), variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const toggleModuleStatus = async () => {
    const newStatus = modStatus === 'draft' ? 'published' : 'draft';
    try {
      const res = await apiFetch(`${API}/api/admin/modules/${moduleId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setModStatus(newStatus);
        toast({ title: `Status: ${newStatus}`, description: newStatus === 'published' ? 'Kurs ist für Kunden sichtbar.' : 'Kurs ist als Entwurf markiert.' });
      }
    } catch (e) {
      toast({ title: 'Status-Update fehlgeschlagen', description: String(e), variant: 'destructive' });
    }
  };

  const openPreview = () => {
    window.open(`/kurs/${moduleId}?admin=1`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-4" data-testid="course-content-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-white">Lektionen bearbeiten</h3>
          <p className="text-sm text-gray-400">Modul: <span className="text-yellow-300">{moduleTitle}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={
              modStatus === 'draft'
                ? 'bg-orange-500/20 text-orange-300 border-orange-500/40'
                : 'bg-green-500/20 text-green-300 border-green-500/40'
            }
            data-testid="module-status-badge"
          >
            {modStatus === 'draft' ? '📝 Entwurf' : '✅ Veröffentlicht'}
          </Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={toggleModuleStatus}
            className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
            data-testid="toggle-status-btn"
          >
            {modStatus === 'draft' ? 'Veröffentlichen' : 'Als Entwurf markieren'}
          </Button>
          <Button
            size="sm"
            className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            onClick={openPreview}
            data-testid="editor-preview-btn"
          >
            <Eye className="h-4 w-4 mr-1" /> Vorschau
          </Button>
        </div>
      </div>

      {modStatus === 'draft' && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
          <AlertTriangle className="h-4 w-4 text-orange-400 mt-0.5 shrink-0" />
          <p className="text-sm text-orange-200">
            <strong>Rechtscheck ausstehend:</strong> Dieser Kurs ist noch nicht veröffentlicht.
            Für Kunden zeigt der Player einen Draft-Banner. Bitte durch Elektro-Fachkraft
            gegenprüfen lassen, bevor du auf &quot;Veröffentlichen&quot; klickst.
          </p>
        </div>
      )}

      {loading && <p className="text-gray-400 text-center py-8">Lektionen werden geladen…</p>}

      {!loading && lessons.length === 0 && (
        <p className="text-gray-400 text-center py-8">Keine Lektionen vorhanden.</p>
      )}

      <div className="space-y-2">
        {lessons.map((lesson) => {
          const isOpen = expandedId === lesson.id;
          const hasDraft = !!lessonDrafts[lesson.id] && Object.keys(lessonDrafts[lesson.id]).length > 0;
          return (
            <div
              key={lesson.id}
              className="border border-yellow-600/20 rounded-lg bg-gray-900/50"
              data-testid={`lesson-editor-row-${lesson.id}`}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-800/40"
                onClick={() => setExpandedId(isOpen ? null : lesson.id)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-yellow-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />}
                  <span className="text-sm text-gray-400 shrink-0">#{lesson.order}</span>
                  <span className="text-white truncate">{getFieldValue(lesson, 'title') || 'Ohne Titel'}</span>
                  {hasDraft && <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 text-xs shrink-0">Ungespeichert</Badge>}
                </div>
                <span className="text-xs text-gray-500 shrink-0 ml-2">{lesson.duration_minutes || 0} Min</span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 pt-2 space-y-3 border-t border-yellow-600/10">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-3">
                      <Label className="text-gray-300 text-xs">Titel</Label>
                      <Input
                        value={getFieldValue(lesson, 'title')}
                        onChange={(e) => updateDraft(lesson.id, 'title', e.target.value)}
                        className="bg-gray-800 border-yellow-600/20 text-white"
                        data-testid={`lesson-title-input-${lesson.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 text-xs">Dauer (Min)</Label>
                      <Input
                        type="number"
                        value={getFieldValue(lesson, 'duration_minutes')}
                        onChange={(e) => updateDraft(lesson.id, 'duration_minutes', parseInt(e.target.value) || 0)}
                        className="bg-gray-800 border-yellow-600/20 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300 text-xs">Moderator-Skript (Lisa TTS)</Label>
                    <Textarea
                      value={getFieldValue(lesson, 'moderator_script')}
                      onChange={(e) => updateDraft(lesson.id, 'moderator_script', e.target.value)}
                      className="bg-gray-800 border-yellow-600/20 text-white font-mono text-xs"
                      rows={4}
                      placeholder="Text, den Lisa dem Lernenden vorliest…"
                      data-testid={`lesson-moderator-input-${lesson.id}`}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-300 text-xs">Content-HTML</Label>
                    <Textarea
                      value={getFieldValue(lesson, 'content_html')}
                      onChange={(e) => updateDraft(lesson.id, 'content_html', e.target.value)}
                      className="bg-gray-800 border-yellow-600/20 text-white font-mono text-xs"
                      rows={12}
                      placeholder="<div class='lesson-content'>…</div>"
                      data-testid={`lesson-content-input-${lesson.id}`}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-300 text-xs">Wissens-Checks (Radio-Button: richtige Antwort auswählen)</Label>
                    <QuizQuestionEditor
                      value={(() => {
                        const draft = lessonDrafts[lesson.id]?.knowledge_checks;
                        if (draft !== undefined) {
                          if (typeof draft === 'string') {
                            try { return JSON.parse(draft); } catch { return []; }
                          }
                          return draft;
                        }
                        return lesson.knowledge_checks || [];
                      })()}
                      onChange={(next) => updateDraft(lesson.id, 'knowledge_checks', next)}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      onClick={() => saveLesson(lesson)}
                      disabled={savingId === lesson.id || !hasDraft}
                      className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400 disabled:opacity-40"
                      data-testid={`lesson-save-btn-${lesson.id}`}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {savingId === lesson.id ? 'Speichert…' : 'Änderungen speichern'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CourseContentEditor;
